/**
 * ─── VENDORED FROM arrow-fe ────────────────────────────────────────────────
 *
 * Source of truth: `arrow-fe/src/lib/lib/crypto/envelope.ts`.
 *
 * This is a copy, not a fork. The panel speaks the same sealed-envelope
 * protocol as the storefront (docs/backend/ENCRYPTION.md), and two
 * implementations of a security protocol that drift is a real liability — so
 * changes belong upstream first, then get copied here.
 *
 * If this ever needs to diverge from the storefront's copy, that is the signal
 * to stop copying and extract a shared workspace package instead
 * (ADMIN.md §4). Do not "fix" it here in isolation.
 * ───────────────────────────────────────────────────────────────────────────
 */

/**
 * The browser half of the API's payload encryption.
 *
 * This is the mirror of `arrow-be/src/lib/crypto/envelope.ts`. The backend
 * speaks it with `node:crypto`; this speaks it with WebCrypto, which is all a
 * browser has. Same curve, same KDF, same cipher, same additional-data string —
 * a difference in any one of them shows up as `ENCRYPTION_FAILED` on every
 * request, so change the two together or not at all.
 *
 *   ECDH(P-256) → HKDF-SHA256 → AES-256-GCM
 *
 * Per request: mint an ephemeral P-256 keypair, agree a secret with the
 * server's published key, and stretch it into a request key and a response key.
 * Only the ephemeral public key goes on the wire.
 *
 * ── What this is for, and what it is not ───────────────────────────────────
 *
 * It keeps payloads out of the Network tab, makes the catalogue meaningfully
 * harder to scrape, and — with the server's nonce cache — stops a captured
 * request being replayed.
 *
 * It is not confidentiality against the person using the browser. The key
 * material is derived in this file, in their tab, and anyone who opens the
 * console can read it. That is not a flaw in the scheme; it is what "the client
 * holds the key" means, and no in-browser design escapes it. Anything that must
 * stay secret from the user stays on the server.
 */

/**
 * TypeScript 5.7 parameterised `Uint8Array` by its backing buffer, and
 * WebCrypto's `BufferSource` accepts only the `ArrayBuffer`-backed one — a bare
 * `Uint8Array` widens to `ArrayBufferLike`, which includes `SharedArrayBuffer`
 * and is rejected. Naming it once keeps that off every signature below.
 */
type Bytes = Uint8Array<ArrayBuffer>;

const LABEL = "arrow/envelope/v1";
const VERSION = 1;

const IV_BYTES = 12;
const NONCE_BYTES = 16;
const TAG_BITS = 128;

export type Handshake = {
  v: number;
  kid: string;
  alg: string;
  /** Base64url DER SPKI — what `importKey("spki", …)` wants. */
  publicKey: string;
  mode: "required" | "optional" | "off";
};

export type Envelope = {
  v: number;
  kid: string;
  epk: string;
  iv: string;
  n: string;
  ts: number;
  /** Absent on a binary upload: the ciphertext is the request body instead. */
  ct?: string;
};

export type ResponseEnvelope = { v: number; iv: string; ct: string };

export type Session = {
  kid: string;
  ts: number;
  nonce: string;
  method: string;
  mode: "json" | "bin";
  requestKey: CryptoKey;
  responseKey: CryptoKey;
};

/** The request payload, both halves in one place regardless of HTTP method. */
export type Payload = {
  body?: unknown;
  query?: Record<string, unknown>;
};

const encoder = new TextEncoder();
const decoder = new TextDecoder();

/**
 * `btoa` works on binary strings, so the bytes are walked rather than spread —
 * `String.fromCharCode(...bytes)` overflows the call stack somewhere north of a
 * hundred thousand arguments, which a large payload reaches.
 */
export function toB64Url(bytes: Uint8Array | ArrayBuffer): string {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);

  let binary = "";
  for (let i = 0; i < view.length; i += 1) {
    binary += String.fromCharCode(view[i] as number);
  }

  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function fromB64Url(value: string): Bytes {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);

  return Uint8Array.from(atob(padded), (character) => character.charCodeAt(0));
}

/**
 * The additional authenticated data.
 *
 * Not encrypted — authenticated. These fields are visible on the wire anyway
 * (the method is the method), and binding them into the AEAD is what stops a
 * captured envelope being replayed as a different kind of request, or a 200
 * body being presented as a 500.
 *
 * Byte-for-byte identical to `aad()` on the server. The trailing empty field on
 * a request is the status slot, and it must stay.
 */
function aad(direction: "request" | "response", session: Session, status?: number): Bytes {
  return encoder.encode(
    [
      LABEL,
      direction,
      session.mode,
      session.kid,
      session.method.toUpperCase(),
      String(session.ts),
      session.nonce,
      status === undefined ? "" : String(status),
    ].join("\n"),
  );
}

async function deriveKeys(
  handshake: Handshake,
  salt: Bytes,
): Promise<{ epk: string; requestKey: CryptoKey; responseKey: CryptoKey }> {
  const serverKey = await crypto.subtle.importKey(
    "spki",
    fromB64Url(handshake.publicKey),
    { name: "ECDH", namedCurve: "P-256" },
    false,
    [],
  );

  // Fresh for every request. Reusing one would let two requests be linked, and
  // would make a single compromised key retroactively readable.
  const ephemeral = await crypto.subtle.generateKey({ name: "ECDH", namedCurve: "P-256" }, true, [
    "deriveBits",
  ]);

  const shared = await crypto.subtle.deriveBits(
    { name: "ECDH", public: serverKey },
    ephemeral.privateKey,
    256,
  );

  const hkdf = await crypto.subtle.importKey("raw", shared, "HKDF", false, ["deriveBits"]);

  const stretch = async (info: string): Promise<CryptoKey> =>
    crypto.subtle.importKey(
      "raw",
      await crypto.subtle.deriveBits(
        { name: "HKDF", hash: "SHA-256", salt, info: encoder.encode(info) },
        hkdf,
        256,
      ),
      "AES-GCM",
      false,
      ["encrypt", "decrypt"],
    );

  return {
    epk: toB64Url(await crypto.subtle.exportKey("raw", ephemeral.publicKey)),
    // Two keys from one secret: sharing a key across both directions means the
    // client and the server can pick the same IV, and an IV collision under one
    // AES-GCM key leaks plaintext outright.
    requestKey: await stretch(`${LABEL}/request/${handshake.kid}`),
    responseKey: await stretch(`${LABEL}/response/${handshake.kid}`),
  };
}

async function startSession(
  handshake: Handshake,
  method: string,
  mode: "json" | "bin",
): Promise<{ session: Session; header: Envelope; iv: Bytes }> {
  const salt = crypto.getRandomValues(new Uint8Array(NONCE_BYTES));
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const { epk, requestKey, responseKey } = await deriveKeys(handshake, salt);

  const session: Session = {
    kid: handshake.kid,
    // The server checks this against a two-minute window. A machine with a
    // badly wrong clock fails every request, which is the intended outcome.
    ts: Date.now(),
    nonce: toB64Url(salt),
    method,
    mode,
    requestKey,
    responseKey,
  };

  return {
    session,
    header: {
      v: VERSION,
      kid: session.kid,
      epk,
      iv: toB64Url(iv),
      n: session.nonce,
      ts: session.ts,
    },
    iv,
  };
}

/** Seals `{ body, query }` into a complete envelope. */
export async function sealRequest(
  handshake: Handshake,
  method: string,
  payload: Payload,
): Promise<{ session: Session; envelope: Envelope }> {
  const { session, header, iv } = await startSession(handshake, method, "json");

  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv, additionalData: aad("request", session), tagLength: TAG_BITS },
    session.requestKey,
    encoder.encode(JSON.stringify({ body: payload.body, query: payload.query })),
  );

  return { session, envelope: { ...header, ct: toB64Url(ciphertext) } };
}

/**
 * Seals raw bytes for the upload path: the envelope header travels in `?d=`
 * with no `ct`, and the ciphertext becomes the request body.
 */
export async function sealBinary(
  handshake: Handshake,
  method: string,
  frame: Bytes,
): Promise<{ session: Session; header: Envelope; body: ArrayBuffer }> {
  const { session, header, iv } = await startSession(handshake, method, "bin");

  const body = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv, additionalData: aad("request", session), tagLength: TAG_BITS },
    session.requestKey,
    frame,
  );

  return { session, header, body };
}

/** Opens a sealed response. Throws if the tag does not verify. */
export async function openResponse(
  session: Session,
  envelope: ResponseEnvelope,
  status: number,
): Promise<unknown> {
  const plaintext = await crypto.subtle.decrypt(
    {
      name: "AES-GCM",
      iv: fromB64Url(envelope.iv),
      additionalData: aad("response", session, status),
      tagLength: TAG_BITS,
    },
    session.responseKey,
    fromB64Url(envelope.ct),
  );

  return JSON.parse(decoder.decode(plaintext));
}

/** The `?d=` form: the envelope, base64url'd, as a single query parameter. */
export function toQueryParam(envelope: Envelope): string {
  return toB64Url(encoder.encode(JSON.stringify(envelope)));
}

/** Narrows a parsed response body to a sealed envelope. */
export function isResponseEnvelope(value: unknown): value is ResponseEnvelope {
  return (
    typeof value === "object" &&
    value !== null &&
    "iv" in value &&
    "ct" in value &&
    typeof (value as { iv: unknown }).iv === "string" &&
    typeof (value as { ct: unknown }).ct === "string"
  );
}
